import React, { useMemo, useState } from "react";
import { Edital, isEditalAberto } from "../types";
import { Calendar, MapPin, Layers, Archive, Clock } from "lucide-react";

interface EditaisSelectorProps {
  editais: Edital[];
  selectedEditalId: string | null;
  onSelectEdital: (id: string) => void;
}

type TabFiltro = "abertos" | "encerrados" | "todos";

export const EditaisSelector: React.FC<EditaisSelectorProps> = ({
  editais,
  selectedEditalId,
  onSelectEdital,
}) => {
  // Separação determinística entre Editais Abertos e Encerrados
  const editaisAbertos = useMemo(
    () => editais.filter((ed) => isEditalAberto(ed.situacao)),
    [editais]
  );

  const editaisEncerrados = useMemo(
    () => editais.filter((ed) => !isEditalAberto(ed.situacao)),
    [editais]
  );

  // Determina a aba inicial: prioriza "abertos" se houver algum, senão "encerrados"
  const [activeTab, setActiveTab] = useState<TabFiltro>(() => {
    return editais.some((ed) => isEditalAberto(ed.situacao)) ? "abertos" : "encerrados";
  });

  // Alterna a aba e, se o edital selecionado não estiver na nova aba, seleciona o primeiro disponível
  const handleTabChange = (novaAba: TabFiltro) => {
    setActiveTab(novaAba);

    if (novaAba === "abertos" && editaisAbertos.length > 0) {
      const jaContem = editaisAbertos.some((e) => e.id === selectedEditalId);
      if (!jaContem) {
        onSelectEdital(editaisAbertos[0].id);
      }
    } else if (novaAba === "encerrados" && editaisEncerrados.length > 0) {
      const jaContem = editaisEncerrados.some((e) => e.id === selectedEditalId);
      if (!jaContem) {
        onSelectEdital(editaisEncerrados[0].id);
      }
    }
  };

  const renderCard = (ed: Edital) => {
    const isSelected = ed.id === selectedEditalId;
    const isAberto = isEditalAberto(ed.situacao);

    return (
      <div
        key={ed.id}
        onClick={() => onSelectEdital(ed.id)}
        className={`edital-card ${isAberto ? "edital-card-aberto" : "edital-card-encerrado"} ${
          isSelected ? "selected" : ""
        }`}
        title={`Clique para carregar os lotes do edital ${ed.id}`}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontWeight: 700,
              fontSize: "0.95rem",
              color: isSelected ? "#60a5fa" : "var(--text-main)",
            }}
          >
            {ed.id}
          </span>
          <span className={`badge ${isAberto ? "badge-ativo" : "badge-encerrado"}`}>
            <span className={`status-dot ${isAberto ? "aberto" : "encerrado"}`} />
            {ed.situacao}
          </span>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            fontSize: "0.8rem",
            color: "var(--text-muted)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <MapPin size={14} color="#60a5fa" />
            <span>Local: {ed.codigo_local}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Calendar size={14} color="#f59e0b" />
            <span>
              Competência: {String(ed.mes).padStart(2, "0")}/{ed.ano}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Layers size={14} color="#34d399" />
            <span>Total: {ed.total_lotes} Lotes</span>
          </div>
        </div>

        {ed.motivo_situacao && (
          <div className="edital-motivo-pill" title={ed.motivo_situacao}>
            <Clock size={12} style={{ flexShrink: 0 }} />
            <span>{ed.motivo_situacao}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="editais-selector-container">
      {/* Cabeçalho do Seletor com Abas de Separação */}
      <div className="editais-header-row">
        <div>
          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--text-main)" }}>
            Editais Públicos Disponíveis
          </h2>
          <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
            Selecione um edital para explorar seus lotes e indicadores
          </span>
        </div>

        {/* Abas Segmentadas: Abertos / Encerrados / Todos */}
        <div className="editais-tab-group" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "abertos"}
            className={`editais-tab-btn ${
              activeTab === "abertos" ? "active active-abertos" : ""
            }`}
            onClick={() => handleTabChange("abertos")}
            title="Exibir apenas editais com leilão aberto ou em andamento"
          >
            <span className="status-dot aberto" />
            <span>Abertos</span>
            <span
              style={{
                fontSize: "0.72rem",
                padding: "2px 6px",
                borderRadius: "10px",
                background:
                  activeTab === "abertos" ? "rgba(16, 185, 129, 0.3)" : "rgba(255, 255, 255, 0.08)",
                color: activeTab === "abertos" ? "#34d399" : "var(--text-muted)",
              }}
            >
              {editaisAbertos.length}
            </span>
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "encerrados"}
            className={`editais-tab-btn ${
              activeTab === "encerrados" ? "active active-encerrados" : ""
            }`}
            onClick={() => handleTabChange("encerrados")}
            title="Exibir histórico de editais encerrados e leilões finalizados"
          >
            <Archive size={14} />
            <span>Encerrados</span>
            <span
              style={{
                fontSize: "0.72rem",
                padding: "2px 6px",
                borderRadius: "10px",
                background:
                  activeTab === "encerrados" ? "rgba(107, 114, 128, 0.4)" : "rgba(255, 255, 255, 0.08)",
                color: activeTab === "encerrados" ? "#e5e7eb" : "var(--text-muted)",
              }}
            >
              {editaisEncerrados.length}
            </span>
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "todos"}
            className={`editais-tab-btn ${activeTab === "todos" ? "active" : ""}`}
            onClick={() => handleTabChange("todos")}
            title="Exibir todos os editais agrupados"
          >
            <span>Todos</span>
            <span
              style={{
                fontSize: "0.72rem",
                padding: "2px 6px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.08)",
                color: "var(--text-muted)",
              }}
            >
              {editais.length}
            </span>
          </button>
        </div>
      </div>

      {/* Conteúdo de acordo com a aba selecionada */}
      {activeTab === "abertos" && (
        <div>
          {editaisAbertos.length > 0 ? (
            <div className="editais-scroll-track">{editaisAbertos.map(renderCard)}</div>
          ) : (
            <div className="edital-empty-banner">
              Nenhum edital com propostas abertas no momento. Acesse a aba{" "}
              <strong>Encerrados</strong> para consultar o histórico de leilões.
            </div>
          )}
        </div>
      )}

      {activeTab === "encerrados" && (
        <div>
          {editaisEncerrados.length > 0 ? (
            <div className="editais-scroll-track">{editaisEncerrados.map(renderCard)}</div>
          ) : (
            <div className="edital-empty-banner">
              Nenhum edital encerrado cadastrado no momento.
            </div>
          )}
        </div>
      )}

      {activeTab === "todos" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Seção de Abertos */}
          <div className="edital-group-section">
            <div className="edital-group-title">
              <span className="status-dot aberto" />
              <span>Editais Abertos / Recebendo Lances ({editaisAbertos.length})</span>
            </div>
            {editaisAbertos.length > 0 ? (
              <div className="editais-scroll-track">{editaisAbertos.map(renderCard)}</div>
            ) : (
              <div className="edital-empty-banner">Nenhum edital aberto no momento.</div>
            )}
          </div>

          {/* Seção de Encerrados */}
          <div className="edital-group-section">
            <div className="edital-group-title">
              <Archive size={14} color="#9ca3af" />
              <span>Editais Encerrados / Histórico ({editaisEncerrados.length})</span>
            </div>
            {editaisEncerrados.length > 0 ? (
              <div className="editais-scroll-track">{editaisEncerrados.map(renderCard)}</div>
            ) : (
              <div className="edital-empty-banner">Nenhum edital encerrado.</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};
